/**
 * Política de senha única da base. A API valida no DTO e no service; o painel
 * usa as mesmas regras no formulário para avisar antes do envio. Mudou aqui,
 * muda nos dois lados.
 */
export const PASSWORD_POLICY = {
  minLength: 8,
  /** O bcrypt só considera os primeiros 72 bytes da senha. */
  maxBytes: 72,
  requireLowercase: true,
  requireUppercase: true,
  requireDigit: true,
  requireSymbol: false,
  /** Usuário ou parte local do e-mail com pelo menos isso de caracteres não pode estar na senha. */
  identityMinLength: 3,
} as const;

export const PASSWORD_VIOLATIONS = [
  'too_short',
  'too_long',
  'missing_lowercase',
  'missing_uppercase',
  'missing_digit',
  'missing_symbol',
  'contains_identity',
  'too_common',
] as const;

export type PasswordViolation = (typeof PASSWORD_VIOLATIONS)[number];

export interface PasswordIdentity {
  email?: string | null;
  username?: string | null;
}

/** Senhas que passam nas regras de composição mas aparecem em todo vazamento. */
const COMMON_PASSWORDS = new Set([
  'password1',
  'password123',
  'passw0rd',
  'p4ssw0rd',
  'qwerty123',
  'qwerty1234',
  'abc12345',
  'abcd1234',
  'admin123',
  'admin1234',
  'administrador1',
  'senha123',
  'senha1234',
  'senha12345',
  'mudar123',
  'mudar1234',
  'mudarsenha1',
  'teste123',
  'teste1234',
  'brasil123',
  'brasil2024',
  'brasil2025',
  'brasil2026',
  'welcome1',
  'welcome123',
  'iloveyou1',
  'changeme1',
  'letmein1',
  'trocar123',
  'acesso123',
]);

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}

function identityTerms(identity: PasswordIdentity): string[] {
  const terms = [
    identity.username,
    identity.email ? identity.email.split('@')[0] : null,
  ];
  return terms
    .map((term) => term?.trim().toLowerCase() ?? '')
    .filter((term) => term.length >= PASSWORD_POLICY.identityMinLength);
}

/** Lista as regras que a senha quebra; vazio quando ela é aceita. */
export function checkPassword(
  password: string,
  identity: PasswordIdentity = {},
): PasswordViolation[] {
  const value = typeof password === 'string' ? password : '';
  const lower = value.toLowerCase();
  const violations: PasswordViolation[] = [];

  if (Array.from(value).length < PASSWORD_POLICY.minLength) {
    violations.push('too_short');
  }
  if (utf8ByteLength(value) > PASSWORD_POLICY.maxBytes) {
    violations.push('too_long');
  }
  if (PASSWORD_POLICY.requireLowercase && !/\p{Ll}/u.test(value)) {
    violations.push('missing_lowercase');
  }
  if (PASSWORD_POLICY.requireUppercase && !/\p{Lu}/u.test(value)) {
    violations.push('missing_uppercase');
  }
  if (PASSWORD_POLICY.requireDigit && !/\p{Nd}/u.test(value)) {
    violations.push('missing_digit');
  }
  if (PASSWORD_POLICY.requireSymbol && !/[^\p{L}\p{N}]/u.test(value)) {
    violations.push('missing_symbol');
  }
  if (identityTerms(identity).some((term) => lower.includes(term))) {
    violations.push('contains_identity');
  }
  if (COMMON_PASSWORDS.has(lower)) {
    violations.push('too_common');
  }

  return violations;
}

export function isPasswordAccepted(
  password: string,
  identity: PasswordIdentity = {},
): boolean {
  return checkPassword(password, identity).length === 0;
}
